import React, { useEffect } from "react";
import styled from "@emotion/styled";
import BackHeader from "@components/header/BackHeader";
import LabelWithInput from "@components/input/LabelWithInput";
import { FormProvider, useForm } from "react-hook-form";
import { DeliveryFeePolicy, PolicyRadioGroup } from "@pages/myPage/_component";
import BasicButton from "@components/button/BasicButton";
import { queryClient } from "@libs/reactQuery/queryClient";
import { useMutation } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import useUserInfoStore from "@store/useUserInfoStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { salesPolicySchema } from "@libs/zod/zodVaildation";

const Container = styled.form({
  display: "flex",
  flexDirection: "column",
});

const ContentWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "4rem",
  padding: "3.5rem 2rem",
  width: "100%",
});

const TextAreaBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",

  ".max": {
    color: theme.color.light_gray,
    fontSize: "1.2rem",
    alignSelf: "flex-end",
  },
}));

const TextArea = styled.textarea(({ theme }) => ({
  fontFamily: "Pretendard ,Sans-serif",
  width: "100%",
  height: "13.8rem",
  padding: "1rem",
  fontSize: "1.6rem",
  lineHeight: "2rem",
  border: `1px solid ${theme.color.light_gray}`,
  resize: "none",

  "::placeholder": {
    color: theme.color.light_gray,
    fontWeight: theme.fontWeight.medium,
  },
}));

const SalesPolicy = () => {
  const method = useForm({
    resolver: zodResolver(salesPolicySchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,

    defaultValues: {
      introduction: "",
      shipping_fee: 0,
      free_shipping_fee: 0,
      is_exchange_allowed: false,
      is_refund_allowed: false,
      is_return_allowed: false,
      notice: "",
    },
  });

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = method;

  const { patchProfile } = useApiRequest();
  const introduction = watch("introduction");
  const notice = watch("notice");
  const navigate = useNavigate();
  const { userInfo } = useUserInfoStore();
  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue("introduction", e.target.value);
  };

  const handleChangeNotice = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue("notice", e.target.value);
  };

  const { mutate: updateProfile } = useMutation({
    mutationFn: (data: any) => patchProfile(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userInfo"] });
      navigate("/my-page");
    },
  });

  const onSubmit = (data: any) => {
    updateProfile(data);
  };

  useEffect(() => {
    setValue("introduction", userInfo?.introduction || "");
    setValue("notice", userInfo?.notice || "");
    setValue("is_exchange_allowed", userInfo?.is_exchange_allowed || false);
    setValue("is_refund_allowed", userInfo?.is_refund_allowed || false);
    setValue("is_return_allowed", userInfo?.is_return_allowed || false);
    setValue("shipping_fee", userInfo?.shipping_fee || 0);
    setValue("free_shipping_fee", userInfo?.free_shipping_fee || 0);
  }, [userInfo]);

  return (
    <FormProvider {...method}>
      <Container onSubmit={handleSubmit(onSubmit)}>
        <BackHeader title={t("판매 정책 설정")} />

        <ContentWrapper>
          <LabelWithInput label={t("소개글")} required>
            <TextAreaBox>
              <TextArea
                name="introduction"
                onChange={handleChange}
                maxLength={2000}
                value={introduction}
                placeholder={t("소개글을 입력해주세요.")}
              />

              <span className="max">
                {introduction.length}/2000{t("자")}
              </span>
            </TextAreaBox>
          </LabelWithInput>

          <LabelWithInput label="정책설정" required gap="2.4rem">
            <PolicyRadioGroup />
          </LabelWithInput>

          <DeliveryFeePolicy />

          <LabelWithInput label="공지사항">
            <TextAreaBox>
              <TextArea
                name="notice"
                onChange={handleChangeNotice}
                maxLength={2000}
                value={notice}
                placeholder={t("공지사항을 입력해주세요.")}
              />

              <span className="max">
                {notice.length}/2000 {t("자")}
              </span>
            </TextAreaBox>
          </LabelWithInput>

          <BasicButton type="submit" title={t("등록")} />
        </ContentWrapper>
      </Container>
    </FormProvider>
  );
};

export default SalesPolicy;
